import {
  ControlledTypeAhead,
  ITypeAheadOption,
} from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PreferencesTab/CustomComponents/ControlledTypeAhead';
import { selectFacility } from '@AMIEWEB/Facility/FacilityStore/Facility.selector';
import {
  selectActiveStep,
  selectEditOrderId,
  selectTypeAheadDropDownOptions,
} from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { orderCreationNewActions } from '@AMIEWEB/NewOrder/Store/OrderCreation.actions';
import { OrderCreationStepEnum } from '@AMIEWEB/NewOrder/types';
import {
  getHelperText,
  SkillSetField,
  validateAMNNurseDuplicateSkillset,
  validateOrderDuplicateSkillset,
} from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitDetailsFormValidator';
import { filterDisciplineList } from '@AMIEWEB/Order/OrderCreationForm/OrderDetails/FormatData';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useDecision } from '@optimizely/react-sdk';
import { Grid } from '@mui/material';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import {
  ff_order_new_page_hide_skills_checklist_checkbox,
  ff_order_ui_allow_duplicate_skillsets,
} from 'app/constants/FeatureFlags/Order/Keys';
import { AMNDivisionType } from 'app/models/enums/AMNDivisionType';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import CardFormComponent from '../Common/CardFormComponent';
import { AndOrOperator } from 'app/enums/Common';

const useStyles = makeStyles()(theme => ({
  fieldStyle: {
    '& .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
  },
  actionButtons: {
    color: `${theme.palette.system.neutralGray}`,
    borderRadius: '20px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${theme.palette.system.skyBlue}`,
      color: `${theme.palette.system.main}`,
    },
  },
}));

export const OrderSkillSetSection = ({ skillSetIndex, groupIndex, skillSetField, onRemove }) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    trigger,
  } = useFormContext();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const typeAheadOptions = useSelector(selectTypeAheadDropDownOptions);
  const facilityValue = useSelector(selectFacility);
  const activeStep = useSelector(selectActiveStep);
  const [isSkillChecklist] = useDecision(ff_order_new_page_hide_skills_checklist_checkbox);
  const [enableDuplicateSkillSet] = useDecision(ff_order_ui_allow_duplicate_skillsets);
  const orderId = useSelector(selectEditOrderId);
  const { pathname } = useLocation();
  const isEdit = !!orderId || pathname.includes('/edit');
  const [filteredDiscipline, setFilteredDiscipline] = React.useState<ITypeAheadOption[]>([]);
  const [filteredSpecialty, setFilteredSpecialty] = React.useState<ITypeAheadOption[]>([]);
  const [filteredSubSpecialty, setFilteredSubSpecialty] = React.useState<ITypeAheadOption[]>([]);
  const disciplineValue = watch(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.discipline`);
  const specialityValue = watch(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.specialty`);
  const skillSetError = errors?.skillSetGroup?.groups?.[groupIndex]?.skillsets?.[skillSetIndex]?.skillSet;
  const skillSets = watch(`skillSetGroup.groups[${groupIndex}].skillsets`);
  const skillSetsPrimary = watch(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].isPrimary`);
  const skillSetValue = watch(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet`);
  const operation = watch(`skillSetGroup.groups[${groupIndex}].operation`);
  const divisionId =
    facilityValue?.facilityResponse?.amnDivisionTypeId ||
    facilityValue?.response?.amnDivisionTypeId ||
    AMNDivisionType.allied;
  const filterDiscipline = () => {
    if (facilityValue) {
      const filteredList = filterDisciplineList(
        divisionId,
        typeAheadOptions?.disciplines,
        typeAheadOptions?.divisionDisciplines,
      );
      setTimeout(() => {
        setFilteredDiscipline(filteredList);
      }, 500);
    } else {
      setFilteredDiscipline(typeAheadOptions?.disciplines);
    }
  };

  const filterSpecialty = (id: number, disableFields) => {
    if (disableFields) {
      setValue(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.specialty`, null);
      setValue(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.subSpecialty`, null);
    }
    const divisionDisciplines = typeAheadOptions?.divisionDisciplines.filter(dd => {
      return (
        (dd.amnDivisionId === facilityValue?.response?.amnDivisionTypeId ||
          dd.amnDivisionId === facilityValue?.facilityResponse?.amnDivisionTypeId) &&
        dd.disciplineId === id
      );
    });
    const filteredSpecialties = divisionDisciplines
      ?.map(dd => {
        return typeAheadOptions?.specialties.find(
          ds => ds?.object?.DisciplineID === dd?.disciplineId && ds?.object?.SpecialtyID === dd?.specialtyId,
        );
      })
      .filter(Boolean);
    return filteredSpecialties || [];
  };

  const filterSubspecialty = (id: number, disableFields) => {
    if (disableFields) {
      setValue(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.subSpecialty`, null);
    }
    const filteredSubSpecialties = typeAheadOptions?.subSpecialties.filter(data => {
      return data?.object?.SpecialtyID === id;
    });
    return filteredSubSpecialties || [];
  };

  useEffect(() => {
    filterDiscipline();
    if (skillSetField?.skillSet?.discipline) {
      setFilteredSpecialty(filterSpecialty(skillSetField?.skillSet?.discipline?.object?.ID, false));
    }
    if (skillSetField?.skillSet?.subSpecialty) {
      setFilteredSubSpecialty(filterSubspecialty(skillSetField?.skillSet?.subSpecialty?.object?.SpecialtyID, false));
    }
  }, [facilityValue]);

  useEffect(() => {
    if ((isEdit || facilityValue) && filteredSubSpecialty.length === 0) {
      setFilteredSubSpecialty(filterSubspecialty(skillSetField?.skillSet?.specialty?.object?.SpecialtyID, false));
    }
  }, [facilityValue, isEdit]);

  useEffect(() => {
    const primary = skillSets?.some(item => item?.isPrimary);
    const isAnd = `${operation}` === AndOrOperator.And;
    if (
      skillSets?.length > 1 &&
      facilityValue?.facilityResponse?.amnDivisionTypeId === AMNDivisionType.amnNurse &&
      activeStep === OrderCreationStepEnum.OrderRequirements
    ) {
      (!enableDuplicateSkillSet.enabled || isAnd) &&
        dispatch(
          globalActions.setBanner({
            message: t('facility.units.unitDetails.unitRequirements.selectOneSkillSet'),
            severity: 'warning',
          }),
        );
      dispatch(orderCreationNewActions.setDisableNextBtnSidePanel(true));
    } else {
      (!enableDuplicateSkillSet.enabled || isAnd) && dispatch(globalActions.closeBanner());
      (!enableDuplicateSkillSet.enabled || isAnd) &&
        dispatch(orderCreationNewActions.setDisableNextBtnSidePanel(false));
    }
    if (primary) {
      dispatch(orderCreationNewActions.setDisableNextBtnSidePanel(false));
    }
  }, [skillSetField, skillSetsPrimary]);

  useEffect(() => {
    if (skillSets?.length === 1 && facilityValue?.facilityResponse?.amnDivisionTypeId === AMNDivisionType.amnNurse) {
      if (!skillSetField?.isPrimary) {
        setValue(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].isPrimary`, true, {
          shouldValidate: true,
        });
      }
    }
  }, [skillSetField]);

  useEffect(() => {
    setValue(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.isRequired`, !!specialityValue, {
      shouldValidate: true,
    });
  }, [groupIndex, setValue, skillSetIndex, specialityValue]);

  useEffect(() => {
    if (enableDuplicateSkillSet.enabled && disciplineValue) {
      trigger([
        `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.specialty`,
        `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.subSpecialty`,
      ]);
    }
  }, [operation]);

  return (
    <CardFormComponent
      title={t('facility.units.unitDetails.unitRequirements.skillSet', { skillCount: skillSetIndex + 1 })}
      icon={
        <Controller
          name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].isPrimary`}
          control={control}
          defaultValue={skillSets?.length === 1 ? true : skillSetField?.isPrimary}
          render={({ field: { onChange, ...rest } }) => (
            <SimpleCheckbox
              isChecked={rest.value}
              handleChecked={event => {
                if (event.target.checked) {
                  for (let i = 0; i < skillSets.length; i++) {
                    setValue(`skillSetGroup.groups[${groupIndex}].skillsets[${i}].isPrimary`, false, {
                      shouldValidate: true,
                    });
                  }
                }
                setValue(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].isPrimary`, true, {
                  shouldValidate: true,
                });
                onChange(event.target.checked);
              }}
              checkboxLabel={t('facility.units.unitDetails.unitRequirements.primarySkillSet')}
              checkboxName={'primarySkillSet'}
              id={`${skillSetIndex}`}
            />
          )}
        />
      }
      newIcon={
        (skillSets || [])?.length > 1 && (
          <DeleteOutlinedIcon
            className={classes.actionButtons}
            onClick={() => {
              onRemove(skillSetIndex);
            }}
          />
        )
      }
      children={
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <Grid container gap={2}>
              {/* First row */}
              <Grid item xs={2.5}>
                <ControlledTypeAhead
                  register={register}
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.discipline`}
                  label={t('facility.units.unitDetails.unitRequirements.discipline')}
                  control={control}
                  options={filteredDiscipline || []}
                  defaultValue={skillSetField?.skillSet?.discipline}
                  onChange={discipline => {
                    setFilteredSpecialty(filterSpecialty(discipline.object.ID, true));
                    clearErrors(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.discipline`);
                    if (enableDuplicateSkillSet.enabled) {
                      clearErrors(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.specialty`);
                      clearErrors(
                        `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.subSpecialty`,
                      );
                      //Revalidate other skillset if first skillset changes
                      if (
                        divisionId === AMNDivisionType.amnNurse &&
                        `${operation}` === AndOrOperator.Or &&
                        skillSetIndex === 0 &&
                        enableDuplicateSkillSet.enabled
                      ) {
                        validateAMNNurseDuplicateSkillset({
                          groupIndex,
                          skillSets,
                          trigger,
                          field: SkillSetField.Discipline,
                        });
                      }
                    }
                  }}
                  isError={Boolean(
                    errors?.skillSetGroup?.groups[groupIndex]?.skillsets[skillSetIndex]?.skillSet?.discipline,
                  )}
                  require={true}
                  helperText={getHelperText({
                    field: SkillSetField.Discipline,
                    error: skillSetError,
                    enableDuplicateSkillSet: enableDuplicateSkillSet.enabled,
                    t,
                  })}
                  isHelperText={true}
                  customScroll
                  disabled={false}
                  id={'orderExperienceDiscipline'}
                  showDropdownIcon={true}
                  validateTypeAhead={() =>
                    validateOrderDuplicateSkillset({
                      skillSetCollection: watch(`skillSetGroup.groups[${groupIndex}].skillsets`),
                      skillSetIndex,
                      operation,
                      enableDuplicateSkillSet: enableDuplicateSkillSet.enabled,
                      divisionId,
                      field: SkillSetField.Discipline,
                      t,
                    })
                  }
                />
              </Grid>
              <Grid item xs={2.5}>
                <ControlledTypeAhead
                  mandatory={false}
                  filledNormal={false}
                  register={register}
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.specialty`}
                  label={t('facility.units.unitDetails.unitRequirements.specialty')}
                  control={control}
                  options={filteredSpecialty || []}
                  defaultValue={skillSetField?.skillSet?.specialty}
                  onChange={specialty => {
                    setFilteredSubSpecialty(filterSubspecialty(specialty.object.SpecialtyID, true));
                    clearErrors(`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.specialty`);
                    if (enableDuplicateSkillSet.enabled) {
                      clearErrors(
                        `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.subSpecialty`,
                      );
                    }
                    //Revalidate other skillset if first skillset changes
                    if (
                      divisionId === AMNDivisionType.amnNurse &&
                      `${operation}` === AndOrOperator.Or &&
                      skillSetIndex === 0 &&
                      enableDuplicateSkillSet.enabled
                    ) {
                      validateAMNNurseDuplicateSkillset({
                        groupIndex,
                        skillSets,
                        trigger,
                        field: SkillSetField.Specialty,
                      });
                    }
                  }}
                  require={true}
                  isError={skillSetError?.specialty}
                  helperText={getHelperText({
                    field: SkillSetField.Specialty,
                    error: skillSetError,
                    enableDuplicateSkillSet: enableDuplicateSkillSet.enabled,
                    t,
                  })}
                  isHelperText={true}
                  disabled={!Boolean(disciplineValue)}
                  customScroll
                  id={'orderExperienceSpecialty'}
                  showDropdownIcon={true}
                  validateTypeAhead={() =>
                    validateOrderDuplicateSkillset({
                      skillSetCollection: watch(`skillSetGroup.groups[${groupIndex}].skillsets`),
                      skillSetIndex,
                      operation,
                      enableDuplicateSkillSet: enableDuplicateSkillSet.enabled,
                      divisionId,
                      field: SkillSetField.Specialty,
                      t,
                    })
                  }
                />
              </Grid>
              <Grid item xs={2.5}>
                <ControlledTypeAhead
                  mandatory={false}
                  filledNormal={false}
                  register={register}
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.subSpecialty`}
                  label={t('facility.units.unitDetails.unitRequirements.subSpecialty')}
                  control={control}
                  options={filteredSubSpecialty || []}
                  defaultValue={skillSetField?.skillSet?.subSpecialty}
                  disabled={
                    skillSetField?.skillSet?.subSpecialty && filteredSubSpecialty?.length
                      ? false
                      : !Boolean(specialityValue) || !Boolean(filteredSubSpecialty?.length)
                  }
                  onChange={subSpecialty => {
                    clearErrors(
                      `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.subSpecialty`,
                    );
                    //Revalidate other skillset if first skillset changes
                    if (
                      divisionId === AMNDivisionType.amnNurse &&
                      `${operation}` === AndOrOperator.Or &&
                      skillSetIndex === 0 &&
                      enableDuplicateSkillSet.enabled
                    ) {
                      validateAMNNurseDuplicateSkillset({
                        groupIndex,
                        skillSets,
                        trigger,
                        field: SkillSetField.SubSpecialty,
                      });
                    }
                  }}
                  isError={skillSetError?.subSpecialty ? skillSetError?.subSpecialty : false}
                  helperText={getHelperText({
                    field: SkillSetField.SubSpecialty,
                    error: skillSetError,
                    enableDuplicateSkillSet: enableDuplicateSkillSet.enabled,
                    value: skillSetValue?.subSpecialty,
                    t,
                  })}
                  validateTypeAhead={() =>
                    validateOrderDuplicateSkillset({
                      skillSetCollection: watch(`skillSetGroup.groups[${groupIndex}].skillsets`),
                      skillSetIndex,
                      operation,
                      enableDuplicateSkillSet: enableDuplicateSkillSet.enabled,
                      divisionId,
                      field: SkillSetField.SubSpecialty,
                      t,
                    })
                  }
                  isHelperText={true}
                  customScroll
                  id={'orderExperienceSubSpecialty'}
                  showDropdownIcon={true}
                />
              </Grid>
              <Grid item xs={1.6}>
                <Controller
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.yearsOfExperience`}
                  control={control}
                  defaultValue={
                    skillSetField?.skillSet?.yearsOfExperience !== 0 ? skillSetField?.skillSet?.yearsOfExperience : null
                  }
                  render={({ field: { onChange, ...rest } }) => (
                    <SingleLineInput
                      id={'orderExperienceInYears'}
                      inputDefaultValue={null}
                      inputLabel={t('facility.units.unitDetails.unitRequirements.experienceIn')}
                      inputName={'ExpYears'}
                      inputValue={rest.value}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const years = parseInt(event.target.value);
                        if (isNaN(years) || years < 0) {
                          onChange('');
                        }
                        if (years >= 0) {
                          onChange(years);
                        }
                      }}
                      showEndAdornment={true}
                      shrinkLabel={true}
                      endAdornmentValue={t('facility.units.unitDetails.unitRequirements.years')}
                      inputType={'number'}
                      fieldStyle={classes.fieldStyle}
                      min={0}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1.6}>
                <Controller
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.monthsOfExperience`}
                  control={control}
                  defaultValue={
                    skillSetField?.skillSet?.monthsOfExperience !== 0
                      ? skillSetField?.skillSet?.monthsOfExperience
                      : null
                  }
                  render={({ field: { onChange, ...rest } }) => (
                    <SingleLineInput
                      id={'orderExperienceInMonths'}
                      inputDefaultValue={null}
                      inputLabel={t('facility.units.unitDetails.unitRequirements.experienceIn')}
                      inputName={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.monthsOfExperience`}
                      inputValue={rest.value}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const months = parseInt(event.target.value);
                        if (isNaN(months) || months < 0) {
                          onChange('');
                        }
                        if (months < 12 && months >= 0) {
                          onChange(months);
                        }
                      }}
                      showEndAdornment={true}
                      shrinkLabel={true}
                      endAdornmentValue={t('facility.units.unitDetails.unitRequirements.months')}
                      inputType={'number'}
                      fieldStyle={classes.fieldStyle}
                      min={0}
                      max={11}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <Grid container gap={2} sx={{ marginTop: '16px' }}>
              <Grid item xs={5}>
                <Controller
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.isRequired`}
                  control={control}
                  defaultValue={skillSetField?.skillSet?.isRequired}
                  render={({ field: { onChange, ...rest } }) => (
                    <SimpleCheckbox
                      isChecked={!!specialityValue}
                      handleChecked={event => {}}
                      checkboxLabel={t('facility.units.unitDetails.unitRequirements.required')}
                      checkboxName={'experienceSkillRequired'}
                      id={'experienceSkillRequired'}
                      isdisabled
                    />
                  )}
                />
              </Grid>
              <Grid item xs={5}>
                <Controller
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.isNewGrad`}
                  control={control}
                  defaultValue={skillSetField?.skillSet?.isNewGrad}
                  render={({ field: { onChange, ...rest } }) => (
                    <SimpleCheckbox
                      isChecked={rest.value ?? skillSetField?.skillSet?.isNewGrad}
                      handleChecked={event => {
                        onChange(event.target.checked);
                      }}
                      checkboxLabel={t('facility.units.unitDetails.unitRequirements.newGrad')}
                      checkboxName={'experienceSkillNewGrad'}
                      id={'experienceSkillNewGrad'}
                    />
                  )}
                />
              </Grid>
              {!isSkillChecklist?.enabled && (
                <Grid item xs={5} sx={{ marginTop: '15px' }}>
                  <Controller
                    name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.isSkillsCheckList`}
                    control={control}
                    defaultValue={skillSetField?.skillSet?.isSkillsCheckList}
                    render={({ field: { onChange, ...rest } }) => (
                      <SimpleCheckbox
                        isChecked={rest.value ?? skillSetField?.skillSet?.isSkillsCheckList}
                        handleChecked={event => {
                          onChange(event.target.checked);
                        }}
                        checkboxLabel={t('facility.units.unitDetails.unitRequirements.skillsChecklist')}
                        checkboxName={'experienceSkillNewGrad'}
                        id={'experienceSkillNewGrad'}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={5} sx={{ marginTop: '15px' }}>
                <Controller
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].skillSet.isClinicalFellowship`}
                  control={control}
                  defaultValue={skillSetField?.skillSet?.isClinicalFellowship}
                  render={({ field: { onChange, ...rest } }) => (
                    <SimpleCheckbox
                      isChecked={rest.value ?? skillSetField?.skillSet?.isClinicalFellowship}
                      handleChecked={event => {
                        onChange(event.target.checked);
                      }}
                      checkboxLabel={t('facility.units.unitDetails.unitRequirements.clinicalFellowship')}
                      checkboxName={'experienceSkillNewGrad'}
                      id={'experienceSkillNewGrad'}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      }
    />
  );
};
